import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { createLogger } from 'redux-logger';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { emptySplitApi } from './emptyApi';
import { configureUserStorage } from '../common/utils/userStorage';
import { rootReducer } from './rootReducer';

// Create logger middleware (only for development)
const loggerMiddleware =
  process.env.NODE_ENV !== 'production'
    ? createLogger({
        collapsed: true,
      })
    : null;

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['repositoryManagement'],
};

// redux-persist@6's Reducer type predates Redux 5 and no longer matches RTK's
// combineReducers output. The mismatch is types-only (runtime is unaffected);
// RootState is derived from rootReducer below so the app stays fully typed.
// @ts-expect-error redux-persist@6 / Redux 5 Reducer type gap
const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  devTools: process.env.NODE_ENV !== 'production',
  reducer: persistedReducer as any,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }).concat(emptySplitApi.middleware, ...(loggerMiddleware ? [loggerMiddleware] : [])),
});

// Wire userStorage to read the driving account id from Redux. During
// impersonation, state.userContext.user is the impersonated account, so
// per-account preference keys (selected_org_id, etc.) naturally swap.
configureUserStorage(() => {
  const user = store.getState().userContext.user;
  const id = user?.id;
  return typeof id === 'number' ? id : null;
});

export const persistor = persistStore(store);

// Enable the refetchOnFocus/refetchOnReconnect behaviors
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export default store;
